/**
 * Fetch with automatic credential inclusion for cross-origin requests.
 * Automatically routes to the correct base path:
 * - /oauth/* -> https://mcp.getbalboa.com/oauth/*
 * - /me -> /api/me (on the same host)
 * - everything else -> https://mcp.getbalboa.com/api/*
 */
export async function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  const MCP_BASE = 'https://mcp.getbalboa.com';
  
  let url: string;

  if (
    path === '/me' ||
    path.startsWith('/me/') ||
    path === '/plans' ||
    path.startsWith('/plans/')
  ) {
    // Session & plan APIs are served from the app worker domain
    url = `/api${path}`;
  } else if (path.startsWith('/oauth')) {
    // OAuth endpoints go to the MCP worker
    url = `${MCP_BASE}${path}`;
  } else {
    // All other API endpoints go to the MCP worker
    url = `${MCP_BASE}/api${path}`;
  }
  
  const headers = new Headers(init?.headers || {});
  const isFormData = (init as any)?.body instanceof FormData;
  // Let the browser set proper multipart boundaries when using FormData
  if (isFormData && headers.has('Content-Type')) {
    headers.delete('Content-Type');
  }

  if (!headers.has('X-Balboa-QA-Key')) {
    const qaKey = (() => {
      if (typeof window === 'undefined') return null;
      try {
        const stored = window.localStorage.getItem('balboa.qaKey');
        return stored ? stored.trim() : null;
      } catch {
        return null;
      }
    })();
    if (qaKey) {
      headers.set('X-Balboa-QA-Key', qaKey);
    }
  }

  return fetch(url, {
    ...init,
    headers,
    credentials: 'include',
    // Add cache control headers to prevent browser caching
    cache: 'no-cache',
  });
}

// ===== Type Definitions =====

export type Project = { 
  projectId: string
  name?: string | null
}

export type ExportItem = {
  projectId: string
  projectName?: string | null
  revision: number
  renderId: string
  createdAt: string
  mp4Url?: string | null
}

export type RenderingItem = {
  renderId: string
  projectId: string
  projectName?: string | null
  revision: number
  status: 'queued' | 'processing' | 'ready' | 'failed'
  startedAt: string
  completedAt?: string | null
  durationSeconds?: number | null
  fileSizeBytes?: number | null
  mp4Url?: string | null
  r2Path?: string | null
  error?: string | null
  costEstimateUsd?: number | null
  createdAt: string
}

export type Asset = {
  id: string
  name: string
  type: 'video' | 'image' | 'audio'
  source?: 'upload' | 'stock' | 'export'
  size?: string
  duration?: string
  url: string
  storageKey?: string
  createdAt?: string
}

export type AssetListResponse = {
  items: Asset[]
  total?: number
  page?: number
  limit?: number
}

export type User = {
  id: string
  email: string
  name?: string
}

// ===== API Helpers =====

/**
 * Get all projects for the current user
 */
export async function getProjects(): Promise<Project[]> {
  const res = await apiFetch('/projects')
  if (!res.ok) throw new Error(`Failed to fetch projects: ${res.statusText}`)
  const payload = await res.json().catch(() => [])
  if (Array.isArray(payload)) return payload
  if (payload && Array.isArray(payload.items)) return payload.items
  return []
}

/**
 * Get exports (renders) for a project or all projects
 */
export async function getExports(projectId?: string): Promise<ExportItem[]> {
  const qs = projectId ? `?projectId=${encodeURIComponent(projectId)}` : ''
  const res = await apiFetch(`/exports${qs}`)
  if (!res.ok) throw new Error(`Failed to fetch exports: ${res.statusText}`)
  const items = await res.json().catch(() => [])
  return Array.isArray(items) ? items : []
}

export type PaginatedRenderings = {
  items: RenderingItem[]
  hasMore: boolean
  meta: {
    currentPage?: number | null
    nextPage?: number | null
    limit?: number | null
    offset?: number | null
  }
}

/**
 * Get renderings for the current user with pagination support
 */
export async function getRenderings(params: { page?: number; limit?: number } = {}): Promise<PaginatedRenderings> {
  const searchParams = new URLSearchParams()
  if (params.page) searchParams.set('page', String(params.page))
  if (params.limit) searchParams.set('limit', String(params.limit))
  const qs = searchParams.toString()

  const res = await apiFetch(`/renderings${qs ? `?${qs}` : ''}`)
  if (!res.ok) throw new Error(`Failed to fetch renderings: ${res.statusText}`)

  const body = await res.json().catch(() => [])
  let items: RenderingItem[] = []
  if (Array.isArray(body)) {
    items = body
  } else if (body && Array.isArray(body.items)) {
    items = body.items
  }

  const parseHeaderNumber = (value: string | null) => {
    if (!value) return null
    const parsed = Number.parseInt(value, 10)
    return Number.isFinite(parsed) ? parsed : null
  }

  const headerHasMore = res.headers.get('X-Has-More')
  const hasMore = typeof body?.hasMore === 'boolean' ? body.hasMore : headerHasMore === 'true'

  const meta =
    body && !Array.isArray(body) && body.meta && typeof body.meta === 'object'
      ? body.meta
      : {
          currentPage: parseHeaderNumber(res.headers.get('X-Page')),
          nextPage: hasMore ? (parseHeaderNumber(res.headers.get('X-Page')) || 1) + 1 : null,
          limit: parseHeaderNumber(res.headers.get('X-Limit')),
          offset: parseHeaderNumber(res.headers.get('X-Offset')),
        }

  return {
    items,
    hasMore,
    meta,
  }
}

/**
 * Get user assets (videos, audio)
 */
export async function getAssets(params?: {
  type?: 'video' | 'audio'
  source?: 'upload' | 'stock' | 'export'
  projectId?: string
  page?: number
  limit?: number
}): Promise<AssetListResponse> {
  const searchParams = new URLSearchParams()
  if (params?.type) searchParams.set('type', params.type)
  if (params?.source) searchParams.set('source', params.source)
  if (params?.projectId) searchParams.set('projectId', params.projectId)
  if (params?.page) searchParams.set('page', String(params.page))
  if (params?.limit) searchParams.set('limit', String(params.limit))
  
  const qs = searchParams.toString()
  const res = await apiFetch(`/assets${qs ? `?${qs}` : ''}`)
  if (!res.ok) throw new Error(`Failed to fetch assets: ${res.statusText}`)
  return await res.json()
}

/**
 * Delete an asset
 */
export async function deleteAsset(assetId: string): Promise<void> {
  const res = await apiFetch(`/assets/${encodeURIComponent(assetId)}`, { method: 'DELETE' })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: { message: 'Delete failed' } }))
    throw new Error(err.error?.message || 'Delete failed')
  }
}

/**
 * Rename an asset
 */
export async function renameAsset(assetId: string, newName: string): Promise<void> {
  const res = await apiFetch(`/assets/${encodeURIComponent(assetId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name: newName })
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Rename failed' }))
    throw new Error(err.error || 'Rename failed')
  }
}

/**
 * Get current user info
 */
export async function getCurrentUser(): Promise<User | null> {
  const res = await apiFetch('/me')
  if (!res.ok) return null
  const payload = await res.json().catch(() => null) as { user?: User | null } | null
  return payload?.user ?? null
}

export type BillingInterval = 'month' | 'year'

export type PlanSummary = {
  id: string
  name: string
  slug: string
  description: string | null
  productId: string | null
  monthlyPriceId: string | null
  yearlyPriceId: string | null
  monthlyPriceCents: number | null
  yearlyPriceCents: number | null
  yearlyDiscountPercent: number | null
  minuteAllowance: number | null
  displayOrder: number
  isFounder: boolean
  requiresInvite: boolean
}

export type SubscriptionSummary = {
  id: string
  planId: string
  planName: string | null
  planSlug: string | null
  minuteAllowance: number | null
  status: string
  billingInterval: 'month' | 'year' | 'lifetime'
  currentPeriodStart: string | null
  currentPeriodEnd: string | null
  cancelAtPeriodEnd: boolean
  stripeCustomerId: string | null
  stripeSubscriptionId: string | null
  priceId: string | null
}

export type PlansOverviewResponse = {
  plans: PlanSummary[]
  subscription: SubscriptionSummary | null
  stripePublishableKey?: string
  defaults?: {
    billingInterval?: BillingInterval
  }
}

export async function fetchPlansOverview(): Promise<PlansOverviewResponse> {
  const res = await apiFetch('/plans')
  if (!res.ok) {
    const message = await res.text().catch(() => res.statusText)
    throw new Error(`Failed to load plans: ${message}`)
  }
  return await res.json()
}

export async function createPlanCheckoutSession(planId: string, interval: BillingInterval): Promise<{ url: string; sessionId?: string }> {
  const res = await apiFetch('/plans/checkout', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ planId, billingInterval: interval }),
  })

  const payload = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new Error(payload?.error || 'Unable to start checkout')
  }

  if (!payload?.url) {
    throw new Error('Stripe checkout URL missing in response')
  }

  return payload
}

export async function validateInviteCode(code: string): Promise<{ valid: boolean; error?: string }> {
  const res = await apiFetch('/plans/invite/validate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  })
  const payload = await res.json().catch(() => ({ valid: false, error: 'Network error' }))
  return payload
}

export async function redeemInviteCode(code: string): Promise<SubscriptionSummary> {
  const res = await apiFetch('/plans/invite', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  })
  const payload = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new Error(payload?.error || 'Invite code was not accepted')
  }
  return payload.subscription as SubscriptionSummary
}

export async function createBillingPortalSession(): Promise<{ url: string }> {
  const res = await apiFetch('/plans/billing-portal', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  })
  const payload = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new Error(payload?.error || 'Unable to create billing portal session')
  }
  if (!payload?.url) {
    throw new Error('Billing portal URL missing in response')
  }
  return payload
}

export type UsageSummary = {
  currentPeriod: {
    start: string
    end: string
  }
  plan: {
    name: string
    minuteAllowance: number
    storageAllowanceGB: number
  }
  usage: {
    minutesUsed: number
    minutesRemaining: number
    storageUsedGB: number
    storageRemainingGB: number
    renderCount: number
  }
  percentages: {
    minutes: number
    storage: number
  }
}

export async function getUsageSummary(): Promise<UsageSummary> {
  const res = await apiFetch('/usage/summary', {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  })
  const payload = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new Error(payload?.error || 'Unable to get usage summary')
  }
  return payload as UsageSummary
}

